// src/components/OwnerCompanyBar.jsx
// Platform owner only: a slim "Company" switch above every company-scoped page.
// The company chosen here is the one whose records the page lists - and the one every add / edit / delete is saved to
// (the id travels with each request as X-Owner-CompanyId). Default = the owner's own company.

import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import { Box, MenuItem, Select, Stack, Typography } from '@mui/material';
import { Building2 } from 'lucide-react';

import { fetchAllCompanies, selectCompany, selectOwnerCompanies, selectSelectedCompanyId } from '../Redux/slices/ownerSlice';

export default function OwnerCompanyBar() {
  const dispatch = useDispatch();
  const { mutate } = useSWRConfig();
  const companies = useSelector(selectOwnerCompanies);
  const selected = useSelector(selectSelectedCompanyId);

  useEffect(() => {
    if (!companies.length) dispatch(fetchAllCompanies());
  }, [companies.length, dispatch]);

  if (!companies.length) return null;
  const value = companies.some((c) => String(c.companyId) === String(selected)) ? String(selected) : '';

  const onChange = (e) => {
    const id = String(e.target.value);
    if (id === value) return;
    dispatch(selectCompany(id));
    // forget everything fetched for the previous company - the page below re-mounts and loads the new one
    mutate((key) => typeof key === 'string' && !key.startsWith('api/menu'), undefined, { revalidate: false });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, minHeight: 30 }}>
      <Building2 size={14} strokeWidth={1.7} color="#8c8c8c" />
      <Typography sx={{ fontSize: 12, color: 'grey.500', whiteSpace: 'nowrap' }}>Company</Typography>
      <Select
        size="small"
        value={value}
        onChange={onChange}
        variant="standard"
        disableUnderline
        sx={{ fontSize: 13, fontWeight: 600, color: 'grey.800', '& .MuiSelect-select': { py: 0.25, pr: '22px !important', bgcolor: 'transparent' }, '& svg': { right: 0 } }}
        renderValue={(v) => companies.find((c) => String(c.companyId) === v)?.name || 'Select company'}
      >
        {companies.map((c) => (
          <MenuItem key={c.companyId} value={String(c.companyId)} sx={{ fontSize: 13 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <span>{c.name}</span>
              {c.isOwnerCompany && <Typography component="span" sx={{ fontSize: 11, color: 'grey.500' }}>(your company)</Typography>}
              {!c.isActive && <Typography component="span" sx={{ fontSize: 11, color: 'error.main' }}>inactive</Typography>}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </Stack>
  );
}
